import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditSupplierModal from "./EditSupplierModal";
import AddSupplierModal from "./AddSupplierModal";
import { ColorRing } from "react-loader-spinner";
import { FaPlus } from "react-icons/fa";

const SuppliersTable = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(buildApiUrl("/api/supplier/suppliers"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.suppliers && Array.isArray(data.suppliers)) {
        setSuppliers(data.suppliers);
      } else {
        setError("Invalid response format. Expected an array of suppliers.");
      }
    } catch (error) {
      setError("An error occurred while fetching suppliers.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredSuppliers = suppliers.filter(
    (supplier) =>
      supplier.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.contactPersonName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSuppliers.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (supplierId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        buildApiUrl(`/api/supplier/suppliers/${supplierId}`),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setSuppliers(
          suppliers.filter((supplier) => supplier._id !== supplierId)
        );
        toast.success("Supplier deleted successfully!");
      } else {
        toast.error("Failed to delete supplier.");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the supplier.");
    }
  };

  const handleAddSupplier = () => {
    setIsAddSupplierModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedSupplier(null);
  };

  const closeAddSupplierModal = () => {
    setIsAddSupplierModalOpen(false);
  };

  return (
    <div className="bg-white">
      <div className="">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between sm:space-x-4 w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search Suppliers..."
              className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full sm:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-700"
            />
            <button
              onClick={handleAddSupplier}
              className="bg-indigo-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-indigo-800 mt-2 sm:mt-0"
            >
              <FaPlus size={12} />
              Add Supplier
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white sticky top-0 z-10">
                <tr>
                  <th className="p-2 text-left font-normal tracking-wide">#</th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Supplier Name
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Contact Person
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Email
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Address
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.map((supplier, index) => (
                  <tr
                    key={supplier._id}
                    className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                  >
                    <td className="px-2 py-0.5">
                      {indexOfFirstItem + index + 1}
                    </td>
                    <td className="px-2 py-0.5 font-semibold">
                      {supplier.supplierName}
                    </td>
                    <td className="px-2 py-0.5">
                      {supplier.contactPersonName}
                    </td>
                    <td className="px-2 py-0.5">{supplier.email}</td>
                    <td className="px-2 py-0.5">{supplier.address}</td>
                    <td className="px-2 py-0.5 text-sm text-center space-x-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(supplier)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <HiOutlinePencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(supplier._id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <HiOutlineTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, suppliers.length)} of {suppliers.length}{" "}
            suppliers
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ToastContainer />
      {isEditModalOpen && selectedSupplier && (
        <EditSupplierModal
          supplier={selectedSupplier}
          onClose={closeEditModal}
          onSubmit={fetchSuppliers}
        />
      )}
      {isAddSupplierModalOpen && (
        <AddSupplierModal
          onClose={closeAddSupplierModal}
          onSubmit={fetchSuppliers}
        />
      )}
    </div>
  );
};

export default React.memo(SuppliersTable);
